import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { I18nProvider } from "@/i18n/I18nProvider";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyPage from "@/pages/VerifyPage";
import VerifyDVRPage from "@/pages/VerifyDVRPage";
import Layout from "@/components/Layout";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyDetailPage from "@/pages/CompanyDetailPage";
import WorkersPage from "@/pages/WorkersPage";
import WorkerDocumentsPage from "@/pages/WorkerDocumentsPage";
import NominationsPage from "@/pages/NominationsPage";
import NominationSignPage from "@/pages/NominationSignPage";
import DeadlinesPage from "@/pages/DeadlinesPage";
import DocumentsPage from "@/pages/DocumentsPage";
import CertificatesPage from "@/pages/CertificatesPage";
import CoursesPage from "@/pages/CoursesPage";
import TemplatesPage from "@/pages/TemplatesPage";
import SignaturesPage from "@/pages/SignaturesPage";
import UsersPage from "@/pages/UsersPage";
import DVRDashboardPage from "@/pages/DVRDashboardPage";
import DVREditorPage from "@/pages/DVREditorPage";
import ImportAttestatiPage from "@/pages/ImportAttestatiPage";
import "@/App.css";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify/:number" element={<VerifyPage />} />
      <Route path="/verify-dvr/:id" element={<VerifyDVRPage />} />
      <Route path="/nominations/sign/:token" element={<NominationSignPage />} />
      <Route
        path="/*"
        element={
          <Protected>
            <Layout>
              <Routes>
                <Route index element={<DashboardPage />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="companies/:id" element={<CompanyDetailPage />} />
                <Route path="workers" element={<WorkersPage />} />
                <Route path="workers/:id/documents" element={<WorkerDocumentsPage />} />
                <Route path="deadlines" element={<DeadlinesPage />} />
                <Route path="attestati" element={<RoleGuard roles={["super_admin", "operatore", "cliente"]}><ImportAttestatiPage /></RoleGuard>} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="certificates" element={<CertificatesPage />} />
                <Route path="courses" element={<CoursesPage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="signatures" element={<SignaturesPage />} />
                <Route path="nominations" element={<RoleGuard roles={["super_admin", "operatore"]}><NominationsPage /></RoleGuard>} />
                <Route path="users" element={<UsersPage />} />
                <Route path="dvr" element={<RoleGuard roles={["super_admin", "operatore"]}><DVRDashboardPage /></RoleGuard>} />
                <Route path="dvr/document/:id" element={<RoleGuard roles={["super_admin", "operatore"]}><DVREditorPage /></RoleGuard>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </Protected>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
